import { strIsNullOrEmpty } from '@fox/atoms';
import { QueryFilter, SortState } from '@fox/data-grid';
import { sendUpdate, useSubscribeTopicWithHandler } from '@fox/extensions';
import { createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { ActionCentreColumn } from './contracts';

export interface ActionCentreContextType {
	filters: QueryFilter[];
	page: number;
	sort: SortState | undefined;
	sourceFilter: string | undefined;
	setFilter: (value: string | undefined, column?: ActionCentreColumn) => void;
	setPage: (page: number) => void;
	setSort: (sort: SortState | undefined) => void;
	setSourceFilter: (sourceFilter: string | undefined) => void;
	clearAllFilters: () => void;
	enableClearFilters: boolean;
}

const ActionCentreContext = createContext<ActionCentreContextType | undefined>(undefined);

// Topics for pub/sub communication
export const ACTION_CENTRE_TOPICS = {
	FILTERS_CLEARED: 'action-centre:filters-cleared'
} as const;

interface ActionCentreContextProviderProps {
	children: ReactNode;
	sourceOptions?: [string, string][];
}

export function ActionCentreContextProvider({ children, sourceOptions }: ActionCentreContextProviderProps) {
	const [filters, setFilters] = useState<QueryFilter[]>([]);
	const [page, setPageState] = useState<number>(1);
	const [sort, setSortState] = useState<SortState | undefined>(undefined);
	const [sourceFilter, setSourceFilterState] = useState<string | undefined>(sourceOptions?.[0]?.[0]);
	const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	const setFilter = useCallback((value: string | undefined, column?: ActionCentreColumn) => {
		const columnIdentifier: 'id' | 'title' = column.type === 'custom' ? 'id' : 'title';

		// Clear existing timeout
		if (debounceTimeoutRef.current) {
			clearTimeout(debounceTimeoutRef.current);
		}

		// Set a new timeout for debouncing
		debounceTimeoutRef.current = setTimeout(() => {
			setFilters(currentFilters => {
				let updatedFilters = [...currentFilters];
				const existingIndex = updatedFilters.findIndex(f => f.dataIndex === column[columnIdentifier]);
				if (existingIndex > -1) {
					if (strIsNullOrEmpty(value)) {
						updatedFilters = updatedFilters?.filter(f => f.dataIndex !== column[columnIdentifier]);
					} else {
						updatedFilters[existingIndex].filterValue = value;
					}
				} else {
					if (!strIsNullOrEmpty(value)) {
						updatedFilters.push({
							dataIndex: column[columnIdentifier],
							filterValue: value,
							filterType: column.filterType
						});
					}
				}

				// Send update to trigger data refetch
				sendUpdate('DATA_REFETCH_NEEDED');
				return updatedFilters;
			});
			setPageState(1);
		}, 1000);
	}, []);

	const setSourceFilter = useCallback((sourceFilter: string | undefined) => {
		setSourceFilterState(sourceFilter);
	}, []);

	const setPage = useCallback((page: number) => {
		setPageState(page);
	}, []);

	const setSort = useCallback((sort: SortState | undefined) => {
		setSortState(sort);
	}, []);

	const clearAllFilters = useCallback(() => {
		setFilters([]);
		setPageState(1);
		// Notify filter components to clear their local state
		sendUpdate(ACTION_CENTRE_TOPICS.FILTERS_CLEARED);
	}, []);

	const contextValue: ActionCentreContextType = {
		filters,
		page,
		sort,
		sourceFilter,
		setFilter,
		setPage,
		setSort,
		setSourceFilter,
		clearAllFilters,
		enableClearFilters: filters.length > 0
	};

	return <ActionCentreContext.Provider value={contextValue}>{children}</ActionCentreContext.Provider>;
}

export function useActionCentreContext(): ActionCentreContextType {
	const context = useContext(ActionCentreContext);
	if (!context) {
		throw new Error('useActionCentreContext must be used within ActionCentreProvider');
	}
	return context;
}

// Hook for listening to filter clear events
export function useFilterClearListener(onClear: () => void) {
	useSubscribeTopicWithHandler(ACTION_CENTRE_TOPICS.FILTERS_CLEARED, onClear);
}
